import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  Chip,
  Button,
  Switch,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import { useTranslation } from "react-i18next";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../firebaseConfig";
import TeacherNavbar from "../components/TeacherNavbar";
import TeacherAddExercise from "../components/TeacherAddExercise";
import TeacherExerciseConversations from "../components/TeacherExerciseConversations";
import TeacherReviewChat from "../components/TeacherReviewChat";
import { updateExercise, deleteExercise } from "../services/exerciseAiService";

const MODE_LABELS = {
  hints: "تلميحات فقط",
  guided: "حل موجّه",
  direct: "إجابة مباشرة",
};

export default function TeacherExercises() {
  const { t } = useTranslation();
  const { groupId } = useParams();
  const [exercises, setExercises] = useState([]);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingExerciseId, setEditingExerciseId] = useState(null);
  const [reviewingExerciseId, setReviewingExerciseId] = useState(null);
  const [selectedExerciseId, setSelectedExerciseId] = useState(null);
  const [deletingExerciseId, setDeletingExerciseId] = useState(null);

  useEffect(() => {
    if (!groupId) return undefined;

    const exercisesRef = query(
      collection(db, "groups", groupId, "exercises"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(exercisesRef, (snapshot) => {
      setExercises(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    return unsubscribe;
  }, [groupId]);

  const selectedExercise = exercises.find((exercise) => exercise.id === selectedExerciseId) || null;

  const handleToggleAi = async (exercise, event) => {
    event.stopPropagation();
    try {
      await updateExercise({
        groupId,
        exerciseId: exercise.id,
        aiEnabled: !exercise.aiEnabled,
      });
    } catch (error) {
      console.error("Unable to toggle Darssy AI:", error);
    }
  };

  const handleDeleteExercise = async (exercise, event) => {
    event.stopPropagation();
    const confirmed = window.confirm(
      t('هل أنت متأكد من حذف تمرين "{{title}}"؟ سيتم حذف كل محادثات الطلاب المرتبطة به نهائياً.', {
        title: exercise.title,
      })
    );
    if (!confirmed) return;

    setDeletingExerciseId(exercise.id);
    try {
      await deleteExercise({ groupId, exerciseId: exercise.id });
      if (selectedExerciseId === exercise.id) {
        setSelectedExerciseId(null);
      }
    } catch (error) {
      console.error("Unable to delete exercise:", error);
      window.alert(error.message || t("تعذّر حذف التمرين."));
    } finally {
      setDeletingExerciseId(null);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f6f7fb" }}>
      <TeacherNavbar />

      <Box sx={{ maxWidth: 1100, mx: "auto", px: { xs: 2, md: 4 }, py: 4, direction: "rtl" }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            mb: 2,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 22 }}>{t("تمارين Darssy AI")}</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsAddOpen(true)}>
            {t("تمرين جديد")}
          </Button>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {exercises.map((exercise) => (
            <Card key={exercise.id} variant="outlined" sx={{ borderRadius: 3 }}>
              <CardActionArea
                onClick={() =>
                  setSelectedExerciseId(exercise.id === selectedExerciseId ? null : exercise.id)
                }
                sx={{
                  p: 2,
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "flex-start", sm: "center" },
                  justifyContent: "space-between",
                  gap: { xs: 1.5, sm: 0 },
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>{exercise.title}</Typography>
                  <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                    {exercise.category} · {t(MODE_LABELS[exercise.mode] || MODE_LABELS.hints)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Chip
                    size="small"
                    color={exercise.reviewStatus === "validated" ? "success" : "warning"}
                    label={exercise.reviewStatus === "validated" ? t("تم التحقق") : t("غير مُراجَع")}
                  />
                  <Chip
                    size="small"
                    color={exercise.aiEnabled ? "success" : "default"}
                    label={exercise.aiEnabled ? t("مفعّل") : t("معطّل")}
                  />
                  <FormControlLabel
                    onClick={(event) => event.stopPropagation()}
                    control={
                      <Switch
                        checked={Boolean(exercise.aiEnabled)}
                        onChange={(event) => handleToggleAi(exercise, event)}
                      />
                    }
                    label=""
                  />
                  <IconButton
                    size="small"
                    title={t("مراجعة والتحقق")}
                    onClick={(event) => {
                      event.stopPropagation();
                      setReviewingExerciseId(exercise.id);
                    }}
                  >
                    <FactCheckIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      setEditingExerciseId(exercise.id);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    title={t("حذف التمرين")}
                    disabled={deletingExerciseId === exercise.id}
                    onClick={(event) => handleDeleteExercise(exercise, event)}
                    sx={{ color: "action.active", "&:hover": { color: "error.main" } }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </CardActionArea>

              {selectedExercise?.id === exercise.id && (
                <Box sx={{ px: 2, pb: 2 }}>
                  <TeacherExerciseConversations groupId={groupId} exerciseId={exercise.id} />
                </Box>
              )}
            </Card>
          ))}

          {exercises.length === 0 && (
            <Typography sx={{ color: "#94a3b8", textAlign: "center", mt: 4 }}>
              {t("لا توجد تمارين بعد. أضف أول تمرين لتفعيل Darssy AI.")}
            </Typography>
          )}
        </Box>
      </Box>

      {isAddOpen && (
        <TeacherAddExercise
          groupId={groupId}
          onClose={() => setIsAddOpen(false)}
          onCreated={() => {}}
        />
      )}

      {editingExerciseId && (
        <TeacherAddExercise
          groupId={groupId}
          exerciseId={editingExerciseId}
          onClose={() => setEditingExerciseId(null)}
          onCreated={() => {}}
        />
      )}

      {reviewingExerciseId && (
        <TeacherReviewChat
          groupId={groupId}
          exerciseId={reviewingExerciseId}
          onClose={() => setReviewingExerciseId(null)}
          onValidated={() => {}}
        />
      )}
    </div>
  );
}
